<template>
  <v-container fluid>
    <!-- FACULTY SELECTOR -->
    <v-row justify="center" class="mb-4">
      <v-col cols="12" sm="6" md="5">
        <v-autocomplete
          v-model="selectedFaculty"
          :items="facultyDropdownItems"
          item-text="text"
          item-value="value"
          label="Select Faculty"
          outlined
          dense
          clearable
          @change="refreshAll"
          :loading="loadingFaculty"
          :search-input.sync="facultySearch"
          hide-no-data
        />
      </v-col>
    </v-row>

    <!-- BAR CHART (Current AY) -->
    <lazy-observer :onVisible="fetchCurrentAY">
      <template v-slot:default>
        <div v-if="!profileDataLoaded">
          <v-skeleton-loader type="image" />
        </div>
        <div v-else-if="chartData.length">
          <v-row justify="center">
            <v-col cols="12" lg="10">
              <v-toolbar flat dense >
                <v-toolbar-title class="text-h6 font-weight-bold mb-4">
                  Faculty Profile Analytics
                </v-toolbar-title>
              </v-toolbar>
              <v-card-text class="pa-0">
                <div style="width:100%;height:480px;">
                  <chart
                    style="width:100%;height:100%;"
                    auto-resize
                    :options="barChartOptions"
                    :key="barChartKey"
                  />
                </div>
              </v-card-text>
            </v-col>
          </v-row>
        </div>
      </template>
    </lazy-observer>

    <!-- CUMULATIVE LINE CHART -->
    <div class="mt-6">
      <lazy-observer :onVisible="fetchYearlyData">
        <template v-slot:default>
          <div v-if="!cumulativeDataLoaded">
            <v-skeleton-loader type="image" />
          </div>
          <div v-else-if="cumulativeData.length">
            <v-row justify="center">
              <v-col cols="12" lg="10">
                <v-toolbar flat dense>
                  <v-toolbar-title class="text-h6 font-weight-bold mb-6">
                    Faculty Profile – (Cumulative)
                  </v-toolbar-title><br><br>
                </v-toolbar>
                <v-card-text class="pa-0">
                  <div style="width:100%;height:480px;">
                    <chart
                      ref="cumulativeChart"                   
                      style="width:100%;height:100%;"
                      auto-resize
                      :options="cumulativeChartOptions"
                      :key="cumulativeChartKey"
                    />
                  </div>
                </v-card-text>
              </v-col>
            </v-row>
          </div>
        </template>
      </lazy-observer>
    </div>

    <!-- NO DATA -->
    <div v-if="allDataLoaded && !chartData.length && !cumulativeData.length">
      <v-alert type="warning" border="left" class="mt-4">
        No data available for the selected faculty(ies).
      </v-alert>
    </div>

    <!-- SNACKBAR -->
    <v-snackbar v-model="snackbar" :color="snackColor" timeout="3000">
      {{ snackMsg }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "FacultyProfile",
  props: [
    "selected_ay",
    "render1",
    "selected_semester",
    "selected_standard",
    "selected_div",
    "selected_course",
    "selected_learner"
  ],
  
  data() {
    return {
      selectedFaculty: "all",
      selectedAy: this.selected_ay || null,
      facultySearch: '',
      facultyList: [],
      ayList: [],

      // Bar chart (current AY)
      chartData: [],
      profileDataLoaded: false,
      barChartKey: 0,

      // Cumulative line chart
      yearlyData: [],
      cumulativeData: [],
      cumulativeDataLoaded: false,
      cumulativeChartKey: 0,

      // Loading & state
      loadingFaculty: false,
      allDataLoaded: false,

      // Caching
      cache: {
        currentAY: {},
        yearly: {}
      },

      // Snackbar
      snackbar: false,
      snackColor: "",
      snackMsg: ""
    };
  },

  computed: {
    defaultFacultyId() {
      return this.facultyList.length ? this.facultyList[0].id : "all";
    },
    
    facultyDropdownItems() {
      const all = [{ text: "All", value: "all" }];
      const list = this.facultyList.map(f => ({
        text: f.name,
        value: f.id
      }));
      return all.concat(list);
    },

    filterKey() {
      return `${this.selectedFaculty}|${this.selectedAy || ""}|${this.selected_standard || ""}|${this.selected_div || ""}`;
    },

    barChartOptions() {
      const categories = ["Awards", "Publications", "Training"];
      let maxVal = 0;

      const series = this.chartData.map((d, i) => {
        const color = this.getColor(i);
        const data = [d.awards, d.publications, d.training];
        maxVal = Math.max(maxVal, ...data);
        return {
          name: d.name,
          type: "bar",
          barWidth: 28,
          itemStyle: { color, borderRadius: [0, 5, 5, 0] },
          data: data.map(v => ({ value: v, itemStyle: { color } }))
        };
      });

      return {
        title: {
          text: this.selectedFaculty === "all"
            ? "All Faculties"
            : `${this.chartData[0].name || "Faculty"}'s Profile`,
          left: "center",
          textStyle: { fontSize: 16, fontWeight: "bold" }
        },
        tooltip: {
          trigger: "axis",
          formatter: params => {
            let s = `<strong>${params[0].name}</strong><br/>`;
            params.forEach(p => (s += `${p.marker} ${p.seriesName}: <strong>${p.value}</strong><br/>`));
            return s;
          }
        },
        legend: { show: false },
        grid: { left: "10%", right: "10%", bottom: "15%", top: "18%", containLabel: true },
        xAxis: {
          type: "value",
          name: "Count",
          nameLocation: "middle",
          nameGap: 30,
          max: Math.max(maxVal + 5, 50)
        },
        yAxis: { type: "category", data: categories },
        series
      };
    },
    cumulativeChartOptions() {

  const categories = ["Awards", "Publications", "Training"];
  const sortedData = [...this.cumulativeData].sort((a, b) => a.ay.localeCompare(b.ay));

  const series = sortedData.map((item, index) => {
    const color = this.getColor(index);
    return {
      name: item.ay,
      type: "line",
      smooth: true,
      symbol: "circle",
      symbolSize: 10,
      lineStyle: { width: 4 },
      itemStyle: { color: color },
      label: {
        show: true,
        position: "top",
        fontSize: 13,
        fontWeight: "bold",
        color: color
      },
      emphasis: {
        focus: "series",
        itemStyle: { borderWidth: 4 }
      },
      data: [
        { value: item.awards, itemStyle: { color } },
        { value: item.publications, itemStyle: { color } },
        { value: item.training, itemStyle: { color } }
      ]
    };
  });

  const facultyName =
    this.selectedFaculty === "all"
      ? "All Faculties"
      : this.facultyList.find(f => f.id === Number(this.selectedFaculty)).name || "Faculty";

  return {
    title: {
      text: `${facultyName} – Cumulative Progress`,
      left: "center",
      textStyle: { fontSize: 18, fontWeight: "bold", color: "#333" }
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", label: { backgroundColor: "#6a7985" } },
      formatter: params => {
        let s = `<strong>${params[0].seriesName}</strong><br/>`;
        params.forEach(p => {
          s += `${p.marker} ${p.name}: <strong>${p.value}</strong><br/>`;
        });
        return s;
      }
    },
    legend: {
      top: 30,
      textStyle: { fontSize: 13 },
      data: sortedData.map(d => d.ay)
    },
    grid: {
      left: "10%",
      right: "10%",
      bottom: "15%",
      top: "22%",
      containLabel: true
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLabel: {
        fontSize: 14,
        fontWeight: "bold",
        rotate: 0
      }
    },
    yAxis: {
      type: "value",
      name: "Cumulative Count",
      nameLocation: "middle",
      nameGap: 50,
      nameTextStyle: { fontSize: 14, fontWeight: "bold" },
      axisLabel: { fontSize: 12 }
    },
    series: series
  };
}
  },

  watch: {
    render1(v) { if (v) this.onload(); },
    selected_ay(v) {
      this.selectedAy = v;
      if (this.facultyList.length) this.onFilterChange();
    },
    selectedFaculty(newVal) {
      if (newVal && this.facultyList.length) this.onFilterChange();
    }
  },

  mounted() {
    this.onload();
window.addEventListener('resize', this.resizeAllCharts);
  },
beforeDestroy() {
    window.removeEventListener('resize', this.resizeAllCharts);
  },
  created() {
    // Register LazyObserver locally
    this.$options.components.LazyObserver = {
      name: "LazyObserver",
      props: {
        onVisible: { type: Function, required: true }
      },
      data() {
        return { loaded: false };
      },
      mounted() {
        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && !this.loaded) {
              this.loaded = true;
              try { this.onVisible(); } catch (e) { console.error(e); }
              observer.disconnect();
            }
          },
          { threshold: 0.15 }
        );
        observer.observe(this.$el);
      },
      render(h) {
        if (this.loaded) return this.$slots.default ? this.$slots.default[0] : h("div");
        return this.$scopedSlots.default
          ? this.$scopedSlots.default()
          : h("div", [h("v-skeleton-loader", { props: { type: "image" } })]);
      }
    };
  },

  methods: {
    showSnack(color, msg) {
      this.snackbar = true;
      this.snackColor = color;
      this.snackMsg = msg;
    },
resizeAllCharts() {
      this.$nextTick(() => {
        // Resize bar chart
        if (this.$refs.barChart && this.$refs.barChart[0] && typeof this.$refs.barChart[0].resize === 'function') {
          this.$refs.barChart[0].resize();
        }

        // Resize cumulative chart
        if (this.$refs.cumulativeChart && this.$refs.cumulativeChart[0] && typeof this.$refs.cumulativeChart[0].resize === 'function') {
          this.$refs.cumulativeChart[0].resize();
        }
      });
    },

    checkAllDataLoaded() {
      this.allDataLoaded = this.profileDataLoaded && this.cumulativeDataLoaded;
    },

    onFilterChange() {
      // Reset loaded flags so lazy observer will trigger reload
      this.profileDataLoaded = false;
      this.cumulativeDataLoaded = false;
      this.allDataLoaded = false;
      this.chartData = [];
      this.yearlyData = [];
      this.cumulativeData = [];
      
      // Force re-render of charts by changing keys
      this.barChartKey = Date.now();
      this.cumulativeChartKey = Date.now() + 1;
    },

    async refreshAll() {
      this.onFilterChange();
      // Trigger immediate fetch if already visible
      if (this.profileDataLoaded === false) {
        await this.fetchCurrentAY();
      }
      if (this.cumulativeDataLoaded === false) {
        await this.fetchYearlyData();
      }
    },

    getCache(section) {
      return this.cache[section][this.filterKey];
    },

    setCache(section, value) {
      this.$set(this.cache[section], this.filterKey, value);
    },

    async onload() {
      this.loadingFaculty = true;
      try {
        const res = await axios.post("/AdminDashboard/facultyProfileAnalytics", {
          orgId: this.$route.params.orgId,
          user: this.$store.getters.user,
          facultyIds: null,
          ay: this.selectedAy,
          standardIds: this.selected_standard ? [this.selected_standard] : [],
          divisionIds: this.selected_div ? [this.selected_div] : []
        });

        if (res.data.flag !== "YES") throw new Error("No data");

        this.facultyList = res.data.faculty_list || [];
        this.ayList = res.data.ay_list || [];

        if (!this.selectedAy && this.ayList.length) {
          this.selectedAy = this.ayList[0].ay;
        }
        
        // Keep "all" as default selection
        if (!this.selectedFaculty) {
          this.selectedFaculty = "all";
        }
        
        // Trigger data fetch for initial load
        await this.fetchCurrentAY();
        await this.fetchYearlyData();
        
      } catch (e) {
        console.error(e);
        this.showSnack("error", "Failed to load initial data");
      } finally {
        this.loadingFaculty = false;
      }
    },

    async fetchCurrentAY() {
      const cached = this.getCache("currentAY");
      if (cached) {
        this.chartData = cached.chartData;
        this.barChartKey = Date.now();
        this.profileDataLoaded = true;
        this.checkAllDataLoaded();
        this.$nextTick(() => this.resizeAllCharts());
        return;
      }

      if (!this.selectedAy) {
        this.profileDataLoaded = true;
        this.checkAllDataLoaded();
        return;
      }

      const facultyIds = this.selectedFaculty === "all" ? null : [Number(this.selectedFaculty)];

      try {
        const res = await axios.post("/AdminDashboard/facultyProfileAnalytics", {
          orgId: this.$route.params.orgId,
          user: this.$store.getters.user,
          facultyIds,
          ay: this.selectedAy, // Always pass ay for current AY data to ensure bar chart shows selected AY counts
          standardIds: this.selected_standard ? [this.selected_standard] : [],
          divisionIds: this.selected_div ? [this.selected_div] : []
        });

        const details = res.data.profile_transaction_details_list || [];

        if (!details.length) {
          this.chartData = [];
          this.profileDataLoaded = true;
          this.checkAllDataLoaded();
          return;
        }

        if (this.selectedFaculty === "all") {
          const aggregate = details.find(d => d.isAggregate);
          const sum = aggregate ? {
            awards: Number(aggregate.awards) || 0,
            publications: Number(aggregate.publications) || 0,
            training: Number(aggregate.training) || 0
          } : details.reduce((acc, d) => {
            acc.awards += Number(d.awards) || 0;
            acc.publications += Number(d.publications) || 0;
            acc.training += Number(d.training) || 0;
            return acc;
          }, { awards: 0, publications: 0, training: 0 });

          this.chartData = [{ name: "All Faculties", ...sum }];
        } else {
          const facultyRows = details.filter(d => d.instructorId === Number(this.selectedFaculty));
          const sum = facultyRows.reduce((acc, d) => {
            acc.awards += Number(d.awards) || 0;
            acc.publications += Number(d.publications) || 0;
            acc.training += Number(d.training) || 0;
            return acc;
          }, { awards: 0, publications: 0, training: 0 });

          const fac = this.facultyList.find(f => f.id === Number(this.selectedFaculty));
          this.chartData = [{
            name: fac.name || "Faculty",
            ...sum
          }];
        }

        this.setCache("currentAY", { chartData: this.chartData });
        this.barChartKey = Date.now();
        this.profileDataLoaded = true;
        this.$nextTick(() => this.resizeAllCharts());
        setTimeout(() => this.resizeAllCharts(), 100);
        setTimeout(() => this.resizeAllCharts(), 300);
      } catch (e) {
        console.error(e);
        this.chartData = [];
        this.profileDataLoaded = true;
      } finally {
        this.checkAllDataLoaded();
      }
    },

    async fetchYearlyData() {
      const cached = this.getCache("yearly");
      if (cached) {
        this.yearlyData = cached.yearlyData;
        this.computeCumulative();
        this.cumulativeChartKey = Date.now();
        this.cumulativeDataLoaded = true;
        this.checkAllDataLoaded();
        this.$nextTick(() => this.resizeAllCharts());
        return;
      }

      const facultyIds = this.selectedFaculty === "all" ? null : [Number(this.selectedFaculty)];

      try {
        const res = await axios.post("/AdminDashboard/facultyProfileAnalytics", {
          orgId: this.$route.params.orgId,
          user: this.$store.getters.user,
          facultyIds,
          standardIds: this.selected_standard ? [this.selected_standard] : [],
          divisionIds: this.selected_div ? [this.selected_div] : []
        });

        const details = res.data.profile_transaction_details_list || [];

        if (!details.length) {
          this.yearlyData = [];
          this.cumulativeData = [];
          this.cumulativeDataLoaded = true;
          this.checkAllDataLoaded();
          return;
        }

        const yearlyMap = new Map();

        details.forEach(item => {
          if (!item.ay) return; // Skip items with null ay to avoid "Unknown"
          const ay = item.ay;
          const current = yearlyMap.get(ay) || { awards: 0, publications: 0, training: 0 };

          const isAggregateRow = item.isAggregate === true;
          const isSelectedFaculty = item.instructorId === Number(this.selectedFaculty);

          if (this.selectedFaculty === "all") {
            if (!isAggregateRow) { // Skip aggregate row to avoid grand total being assigned to a single ay (causes "Unknown" mismatch)
              current.awards += Number(item.awards) || 0;
              current.publications += Number(item.publications) || 0;
              current.training += Number(item.training) || 0;
              yearlyMap.set(ay, current);
            }
          } else {
            if (isSelectedFaculty) {
              current.awards += Number(item.awards) || 0;
              current.publications += Number(item.publications) || 0;
              current.training += Number(item.training) || 0;
              yearlyMap.set(ay, current);
            }
          }
        });

        this.yearlyData = Array.from(yearlyMap.entries())
          .map(([ay, vals]) => ({ ay, ...vals }))
          .sort((a, b) => a.ay.localeCompare(b.ay));

        this.setCache("yearly", { yearlyData: this.yearlyData });
        this.computeCumulative();
        this.cumulativeChartKey = Date.now();
        this.cumulativeDataLoaded = true;
        this.$nextTick(() => this.resizeAllCharts());
        setTimeout(() => this.resizeAllCharts(), 100);
        setTimeout(() => this.resizeAllCharts(), 300);
      } catch (e) {
        console.error(e);
        this.yearlyData = [];
        this.cumulativeData = [];
        this.cumulativeDataLoaded = true;
      } finally {
        this.checkAllDataLoaded();
      }
    },

    computeCumulative() {
      let cumA = 0, cumP = 0, cumT = 0;
      this.cumulativeData = this.yearlyData.map(item => {
        cumA += item.awards;
        cumP += item.publications;
        cumT += item.training;
        return {
          ay: item.ay,
          awards: cumA,
          publications: cumP,
          training: cumT
        };
      });
    },

    getColor(i) {
      const colors = ["#1976d2", "#ff9800", "#4caf50", "#f44336", "#9c27b0", "#00bcd4"];
      return colors[i % colors.length];
    }
  }
};
</script>

<style scoped>
/* .toolbar {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
} */
</style>